// Service to fetch and process Leksaksbilar.se product feed
const PRODUCT_FEED_URL = 'https://adtraction.com/productfeed.htm?type=feed&format=CSV&encoding=UTF8&epi=0&zip=0&cdelim=tab&tdelim=singlequote&sd=0&sn=0&flat=0&apid=1954031374&asid=1971524470&gsh=1&pfid=2602&gt=1';

// Use Netlify function for production, fallback to CORS proxy for development
const isProduction = process.env.NODE_ENV === 'production' || window.location.hostname.includes('netlify.app');
const API_ENDPOINT = isProduction 
  ? '/.netlify/functions/fetch-products'
  : 'https://corsproxy.io/?' + encodeURIComponent(PRODUCT_FEED_URL);

// Cache for products to avoid repeated API calls
let cachedProducts = null;
let lastFetchTime = null;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// Parse CSV line and convert to product object
const parseProductFromCSV = (line) => {
  // Split by tab delimiter and clean up quotes
  const fields = line.split('\t').map(field => field.replace(/'/g, ''));
  
  if (fields.length < 20) return null; // Skip incomplete records
  
  const [
    id, title, description, product_type, google_product_category, 
    link, image_link, condition, availability, price, sale_price, 
    gtin, brand, mpn, item_group_id, gender, age_group, color, 
    material, pattern, size, energy_efficiency_class, shipping, 
    is_bundle, multipack, custom_label_0, custom_label_1, 
    custom_label_2, custom_label_3, custom_label_4, 
    unit_pricing_measure, unit_pricing_base_measure
  ] = fields;

  // Extract price as number
  const priceValue = parseFloat(price?.replace(' SEK', '').replace(',', '.')) || 0;
  const salePriceValue = sale_price ? parseFloat(sale_price.replace(' SEK', '').replace(',', '.')) : null;

  // Determine category based on product type, brand, and name
  let categoryId = 1; // Default to Hot Wheels
  
  const brandLower = brand?.toLowerCase() || '';
  const nameLower = title?.toLowerCase() || '';
  const typeLower = product_type?.toLowerCase() || '';
  
  // Check for LEGO first (most specific)
  if (brandLower.includes('lego') || nameLower.includes('lego')) {
    categoryId = 2; // LEGO
  }
  // Check for RC/Radio controlled cars
  else if (brandLower.includes('rc') || nameLower.includes('rc') || 
           nameLower.includes('radiostyrd') || nameLower.includes('fjärrstyrd') ||
           nameLower.includes('remote control') || nameLower.includes('wireless') ||
           typeLower.includes('rc') || typeLower.includes('radiostyrd')) {
    categoryId = 4; // RC Cars
  }
  // Check for Siku and similar model car brands
  else if (brandLower.includes('siku') || brandLower.includes('majorette') ||
           brandLower.includes('maisto') || brandLower.includes('bburago') ||
           brandLower.includes('motormax') || brandLower.includes('mini gt') ||
           brandLower.includes('tomica') || brandLower.includes('welly')) {
    categoryId = 3; // Siku/Model Cars
  }
  // Check for Hot Wheels specifically
  else if (brandLower.includes('hot wheels') || nameLower.includes('hot wheels') ||
           brandLower.includes('mattel')) {
    categoryId = 1; // Hot Wheels
  }
  // Check for car brands that should be in Hot Wheels category (small scale)
  else if (nameLower.includes('ferrari') || nameLower.includes('lamborghini') ||
           nameLower.includes('porsche') || nameLower.includes('bmw') ||
           nameLower.includes('mercedes') || nameLower.includes('audi') ||
           nameLower.includes('ford') || nameLower.includes('chevrolet') ||
           nameLower.includes('toyota') || nameLower.includes('honda')) {
    categoryId = 1; // Hot Wheels (model cars)
  }
  // Check for LEGO-like building sets
  else if (nameLower.includes('byggsats') || nameLower.includes('bygg') ||
           nameLower.includes('construction') || nameLower.includes('building') ||
           nameLower.includes('brick') || nameLower.includes('block')) {
    categoryId = 2; // LEGO
  }
  // Check for larger vehicles (Siku category)
  else if (nameLower.includes('lastbil') || nameLower.includes('truck') ||
           nameLower.includes('traktor') || nameLower.includes('tractor') ||
           nameLower.includes('byggmaskin') || nameLower.includes('construction vehicle') ||
           nameLower.includes('ambulans') || nameLower.includes('brandbil') ||
           nameLower.includes('police') || nameLower.includes('fire truck')) {
    categoryId = 3; // Siku
  }
  // Check for toy vehicles that should be in Hot Wheels
  else if (nameLower.includes('bil') || nameLower.includes('car') ||
           nameLower.includes('vehicle') || nameLower.includes('fordon')) {
    categoryId = 1; // Hot Wheels
  }

  // Generate rating and reviews (simulated for demo)
  const rating = 4.0 + Math.random() * 1.0; // 4.0-5.0
  const reviews = Math.floor(Math.random() * 200) + 10; // 10-210

  return {
    productId: id,
    image: image_link,
    productName: title,
    brand: brand || 'Okänt märke',
    price: salePriceValue || priceValue,
    originalPrice: salePriceValue ? priceValue : null,
    categoryId: categoryId,
    description: description || 'Ingen beskrivning tillgänglig',
    inStock: availability === 'in_stock',
    rating: Math.round(rating * 10) / 10,
    reviews: reviews,
    affiliateLink: link,
    productType: product_type,
    color: color,
    material: material,
    size: size,
    ageGroup: age_group,
    gtin: gtin
  };
};

// Fetch products from the feed
export const fetchProductsFromFeed = async () => {
  // Check cache first
  if (cachedProducts && lastFetchTime && (Date.now() - lastFetchTime) < CACHE_DURATION) {
    console.log('Using cached products');
    return cachedProducts;
  }

  try {
    console.log(`Fetching products from ${isProduction ? 'Netlify function' : 'CORS proxy'}...`);
    
    if (isProduction) {
      // Use Netlify function
      const response = await fetch(API_ENDPOINT, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Netlify function response:', data);
      
      if (!data.success || !data.products) {
        throw new Error(data.error || 'Invalid response from Netlify function');
      }
      
      const products = data.products;
      console.log(`Successfully loaded ${products.length} products from Netlify function`);
      
      if (products.length === 0) {
        throw new Error('No valid products found in feed');
      }
      
      // Cache the results
      cachedProducts = products;
      lastFetchTime = Date.now();
      
      return products;
    } else {
      // Use CORS proxy for development
      const response = await fetch(API_ENDPOINT, {
        method: 'GET',
        headers: {
          'Accept': 'text/csv',
          'Content-Type': 'text/csv',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const csvText = await response.text();
      console.log('CSV response received, length:', csvText.length);
      
      if (!csvText || csvText.length < 100) {
        throw new Error('Invalid CSV response - too short');
      }
      
      const lines = csvText.split('\n');
      console.log('CSV lines count:', lines.length);
      
      // Skip header row and filter out empty lines
      const productLines = lines.slice(1).filter(line => line.trim().length > 0);
      
      // Parse products
      const allProducts = productLines
        .map(line => parseProductFromCSV(line))
        .filter(product => product && product.productName && product.price > 0);
      
      console.log(`Successfully parsed ${allProducts.length} products from feed`);
      
      if (allProducts.length === 0) {
        throw new Error('No valid products found in feed');
      }
      
      // Curate best products to avoid performance issues
      const products = curateBestProducts(allProducts);
      console.log(`Curated to ${products.length} best products`);
      
      // Cache the results
      cachedProducts = products;
      lastFetchTime = Date.now();
      
      return products;
    }
    
  } catch (error) {
    console.error('Error fetching product feed:', error);
    console.log('Falling back to static products');
    
    // Return fallback data if feed fails
    return getFallbackProducts();
  }
};

// Get products by category
export const getProductsByCategory = async (categoryId) => {
  try {
    console.log(`Getting products for category ${categoryId}...`);
    const allProducts = await Promise.race([
      fetchProductsFromFeed(),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), 10000) // 10 second timeout
      )
    ]);
    
    console.log(`Total products loaded: ${allProducts.length}`);
    const categoryProducts = allProducts.filter(product => product.categoryId === categoryId);
    console.log(`Products for category ${categoryId}: ${categoryProducts.length}`);
    
    if (categoryProducts.length === 0) {
      console.log('No products found for category, using fallback...');
      const fallbackProducts = getFallbackProducts();
      return fallbackProducts.filter(product => product.categoryId === categoryId);
    }
    
    return categoryProducts;
  } catch (error) {
    console.error('Error getting products by category:', error);
    // Return fallback products for this category
    const fallbackProducts = getFallbackProducts();
    return fallbackProducts.filter(product => product.categoryId === categoryId);
  }
};

// Search products
export const searchProducts = async (searchTerm) => {
  try {
    const allProducts = await Promise.race([
      fetchProductsFromFeed(),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), 10000) // 10 second timeout
      )
    ]);
    const term = searchTerm.toLowerCase();
    
    return allProducts.filter(product => 
      product.productName.toLowerCase().includes(term) ||
      product.brand.toLowerCase().includes(term) ||
      product.description.toLowerCase().includes(term) ||
      (product.color && product.color.toLowerCase().includes(term))
    );
  } catch (error) {
    console.error('Error searching products:', error);
    // Return fallback search results
    const fallbackProducts = getFallbackProducts();
    const term = searchTerm.toLowerCase();
    return fallbackProducts.filter(product => 
      product.productName.toLowerCase().includes(term) ||
      product.brand.toLowerCase().includes(term) ||
      product.description.toLowerCase().includes(term)
    );
  }
};

// Get featured products (first 12 products)
export const getFeaturedProducts = async () => {
  const allProducts = await fetchProductsFromFeed();
  return allProducts.slice(0, 12);
};

// Fallback products if feed fails
const getFallbackProducts = () => {
  return [
    // Hot Wheels - Category 1
    {
      productId: 'fallback-1',
      image: '/images/Bild1.jpg',
      productName: 'Hot Wheels Ferrari F40',
      brand: 'Hot Wheels',
      price: 29,
      originalPrice: 35,
      categoryId: 1,
      description: 'Klassisk röd Ferrari F40 i 1:64 skala. Perfekt för samlare och barn.',
      inStock: true,
      rating: 4.8,
      reviews: 124,
      affiliateLink: 'https://leksaksbilar.se'
    },
    {
      productId: 'fallback-2',
      image: '/images/Bild2.jpg',
      productName: 'Hot Wheels Lamborghini Aventador',
      brand: 'Hot Wheels',
      price: 32,
      originalPrice: 38,
      categoryId: 1,
      description: 'Gul Lamborghini Aventador med detaljerad design och smidig rullning.',
      inStock: true,
      rating: 4.9,
      reviews: 89,
      affiliateLink: 'https://leksaksbilar.se'
    },
    {
      productId: 'fallback-3',
      image: '/images/Bild3.jpg',
      productName: 'Hot Wheels Porsche 911 GT3',
      brand: 'Hot Wheels',
      price: 28,
      originalPrice: 33,
      categoryId: 1,
      description: 'Vit Porsche 911 GT3 med realistiska detaljer och premium finish.',
      inStock: true,
      rating: 4.7,
      reviews: 156,
      affiliateLink: 'https://leksaksbilar.se'
    },
    
    // LEGO - Category 2
    {
      productId: 'fallback-4',
      image: '/images/Bild5.jpg',
      productName: 'LEGO Speed Champions Ferrari 488 GT3',
      brand: 'LEGO',
      price: 199,
      originalPrice: 249,
      categoryId: 2,
      description: 'Bygg din egen Ferrari 488 GT3 med 167 delar. Inkluderar minifigur.',
      inStock: true,
      rating: 4.8,
      reviews: 234,
      affiliateLink: 'https://leksaksbilar.se'
    },
    {
      productId: 'fallback-5',
      image: '/images/Bild6.jpg',
      productName: 'LEGO Speed Champions Lamborghini Huracán',
      brand: 'LEGO',
      price: 179,
      originalPrice: 229,
      categoryId: 2,
      description: 'LEGO Lamborghini Huracán med 219 delar och detaljerad interiör.',
      inStock: true,
      rating: 4.9,
      reviews: 189,
      affiliateLink: 'https://leksaksbilar.se'
    },
    
    // Siku - Category 3
    {
      productId: 'fallback-6',
      image: '/images/Bild9.jpg',
      productName: 'Siku Mercedes-Benz Arocs',
      brand: 'Siku',
      price: 89,
      originalPrice: 109,
      categoryId: 3,
      description: 'Realistisk Mercedes-Benz Arocs lastbil i 1:50 skala med funktionella delar.',
      inStock: true,
      rating: 4.7,
      reviews: 78,
      affiliateLink: 'https://leksaksbilar.se'
    },
    {
      productId: 'fallback-7',
      image: '/images/Bild10.jpg',
      productName: 'Siku BMW X5',
      brand: 'Siku',
      price: 45,
      originalPrice: 55,
      categoryId: 3,
      description: 'BMW X5 SUV med öppningsbara dörrar och detaljerad interiör.',
      inStock: true,
      rating: 4.5,
      reviews: 123,
      affiliateLink: 'https://leksaksbilar.se'
    },
    
    // RC Cars - Category 4
    {
      productId: 'fallback-8',
      image: '/images/Bild13.jpg',
      productName: 'RC Bil - Lamborghini Aventador',
      brand: 'Maisto',
      price: 299,
      originalPrice: 399,
      categoryId: 4,
      description: 'Radiostyrd Lamborghini Aventador med 2.4GHz fjärrkontroll och LED-belysning.',
      inStock: true,
      rating: 4.6,
      reviews: 34,
      affiliateLink: 'https://leksaksbilar.se'
    },
    {
      productId: 'fallback-9',
      image: '/images/Bild24.jpg',
      productName: 'RC Bil - Ford Mustang GT',
      brand: 'Maisto',
      price: 249,
      originalPrice: 329,
      categoryId: 4,
      description: 'Radiostyrd Ford Mustang GT med realistiska ljud och rörelser.',
      inStock: true,
      rating: 4.7,
      reviews: 28,
      affiliateLink: 'https://leksaksbilar.se'
    }
  ];
};

// Curate best products from the feed (client-side version)
const curateBestProducts = (allProducts) => {
  console.log('Curating best products from', allProducts.length, 'total products');
  
  // Define limits per category
  const categoryLimits = {
    1: 20, // Hot Wheels - 20 products
    2: 15, // LEGO - 15 products  
    3: 20, // Siku/Majorette - 20 products
    4: 10  // RC Cars - 10 products
  };

  // Group products by category
  const productsByCategory = {};
  allProducts.forEach(product => {
    if (!productsByCategory[product.categoryId]) {
      productsByCategory[product.categoryId] = [];
    }
    productsByCategory[product.categoryId].push(product);
  });

  const curatedProducts = [];

  // Select best products from each category
  Object.keys(productsByCategory).forEach(categoryId => {
    const categoryProducts = productsByCategory[categoryId];
    const limit = categoryLimits[categoryId] || 10;
    
    // Sort by priority criteria
    const sortedProducts = categoryProducts.sort((a, b) => {
      // Priority 1: In stock products first
      if (a.inStock !== b.inStock) {
        return b.inStock - a.inStock;
      }
      
      // Priority 2: Lower price (more accessible)
      if (a.price !== b.price) {
        return a.price - b.price;
      }
      
      // Priority 3: Higher rating
      if (a.rating !== b.rating) {
        return b.rating - a.rating;
      }
      
      // Priority 4: More reviews
      return b.reviews - a.reviews;
    });

    // Take the best products up to the limit
    const bestProducts = sortedProducts.slice(0, limit);
    curatedProducts.push(...bestProducts);
    
    console.log(`Category ${categoryId}: Selected ${bestProducts.length} best products from ${categoryProducts.length} available`);
  });

  // Add some popular brands as priority
  const popularBrands = ['Hot Wheels', 'LEGO', 'Siku', 'Majorette', 'AUTOart', 'Maisto'];
  const brandPriorityProducts = allProducts
    .filter(product => popularBrands.some(brand => 
      product.brand && product.brand.toLowerCase().includes(brand.toLowerCase())
    ))
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 10); // Add 10 more popular brand products

  // Combine curated and popular brand products, removing duplicates
  const allCurated = [...curatedProducts, ...brandPriorityProducts];
  const uniqueProducts = allCurated.filter((product, index, self) => 
    index === self.findIndex(p => p.productId === product.productId)
  );

  console.log(`Final curated selection: ${uniqueProducts.length} products`);
  return uniqueProducts;
};

export default {
  fetchProductsFromFeed,
  getProductsByCategory,
  searchProducts,
  getFeaturedProducts
};
